import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { DriverAdminTabs } from '../../components/admin/DriverAdminTabs';
import { Car, Plus, Phone, CheckCircle2, XCircle, Loader2, Search, Users, Link as LinkIcon, IdCard } from 'lucide-react';
interface Driver {
  id: number;
  full_name: string;
  national_id: string | null;
  phone: string;
  is_active: boolean;
  created_at: string;
  active_vehicle?: any;
}
export function DriversListPage() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  useEffect(() => {
    fetchDrivers();
  }, []);
  const fetchDrivers = async () => {
    setLoading(true);
    const {
      data: driversData,
      error
    } = await supabase.from('drivers').select('id, full_name, national_id, phone, is_active, created_at').order('created_at', {
      ascending: false
    });
    if (!error && driversData) {
      const driversWithVehicles = await Promise.all(driversData.map(async driver => {
        try {
          const {
            data: vehicleData
          } = await supabase.rpc('get_active_vehicle_for_driver', {
            p_driver_id: driver.id
          });
          return {
            ...driver,
            active_vehicle: Array.isArray(vehicleData) ? vehicleData[0] : vehicleData
          };
        } catch (err) {
          return driver;
        }
      }));
      setDrivers(driversWithVehicles);
    }
    setLoading(false);
  };
  const toggleActive = async (driverId: number, currentStatus: boolean) => {
    await supabase.from('drivers').update({
      is_active: !currentStatus
    }).eq('id', driverId);
    fetchDrivers();
  };
  const filtered = drivers.filter(d => d.full_name?.toLowerCase().includes(search.toLowerCase()) || d.phone?.includes(search) || d.national_id?.toLowerCase().includes(search.toLowerCase()));
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-2759">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-2760">
        <DriverAdminTabs data-id="element-2761" />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2762">
          <div className="flex items-center gap-3" data-id="element-2763">
            <div className="w-12 h-12 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-2764">
              <Users className="w-6 h-6 text-[#006847]" data-id="element-2765" />
            </div>
            <div data-id="element-2766">
              <h1 className="text-2xl font-bold" data-id="element-2767">Conductores</h1>
              <p className="text-white/40 text-sm" data-id="element-2768">
                {drivers.length} registrados
              </p>
            </div>
          </div>
          <button onClick={() => navigate('/admin/drivers/new')} className="flex items-center justify-center gap-2 bg-[#006847] hover:bg-[#005a3d] text-white font-bold py-3 px-6 rounded-xl transition-all active:scale-95" data-id="element-2769">
            <Plus className="w-5 h-5" data-id="element-2770" />
            Nuevo Conductor
          </button>
        </div>

        {/* Search */}
        <div className="relative" data-id="element-2771">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-2772" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o teléfono..." className="w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-2773" />
        </div>

        {/* List */}
        {loading ? <div className="flex justify-center py-20" data-id="element-2774">
            <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-2775" />
          </div> : filtered.length === 0 ? <div className="text-center py-20" data-id="element-2776">
            <Car className="w-16 h-16 text-white/10 mx-auto mb-4" data-id="element-2777" />
            <p className="text-white/40 text-lg font-medium" data-id="element-2778">
              {search ? 'Sin resultados' : 'No hay conductores registrados'}
            </p>
          </div> : <div className="space-y-3" data-id="element-2779">
            {filtered.map(driver => <div key={driver.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-2780">
                {/* Avatar + Info */}
                <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-2781">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center shrink-0 text-xl font-black text-white/30" data-id="element-2782">
                    {driver.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0" data-id="element-2783">
                    <h3 className="font-bold text-white truncate" data-id="element-2784">
                      {driver.full_name}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-white/50 mt-1 flex-wrap" data-id="element-2785">
                      {driver.national_id && <span className="flex items-center gap-1" data-id="element-2786">
                          <IdCard className="w-3 h-3" data-id="element-2787" />
                          {driver.national_id}
                        </span>}
                      <span className="flex items-center gap-1" data-id="element-2788">
                        <Phone className="w-3 h-3" data-id="element-2789" />
                        {driver.phone}
                      </span>
                      {driver.active_vehicle ? <span className="flex items-center gap-1.5 bg-[#006847]/20 text-[#00a870] px-2.5 py-0.5 rounded-lg font-medium" data-id="element-2790">
                          <Car className="w-3 h-3" data-id="element-2791" />
                          {driver.active_vehicle.plate} (
                          {driver.active_vehicle.vehicle_type})
                        </span> : <span className="flex items-center gap-1.5 bg-white/5 text-white/40 px-2.5 py-0.5 rounded-lg font-medium" data-id="element-2792">
                          <Car className="w-3 h-3" data-id="element-2793" />
                          Sin vehículo
                        </span>}
                    </div>
                  </div>
                </div>

                {/* Status + Actions */}
                <div className="flex items-center gap-3 shrink-0 mt-4 sm:mt-0" data-id="element-2794">
                  <button onClick={() => navigate('/admin/assignments')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 text-white/70 hover:bg-white/10 transition-colors" data-id="element-2795">
                    <LinkIcon className="w-3.5 h-3.5" data-id="element-2796" /> Asignar
                  </button>
                  <button onClick={() => toggleActive(driver.id, driver.is_active)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${driver.is_active ? 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30' : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'}`} data-id="element-2797">
                    {driver.is_active ? <>
                        <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2798" /> Activo
                      </> : <>
                        <XCircle className="w-3.5 h-3.5" data-id="element-2799" /> Inactivo
                      </>}
                  </button>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
}